'use client'

import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { apiUrlForEnv } from '@riksja/common/urls'

import { DATADOG_CONFIG } from '@config/datadog.config'

import { toDatadogEnv } from '@/utilities/helpers/riksja-environment.helpers'

const entity = process.env.NEXT_PUBLIC_RIKSJA_ENTITY ?? 'riksja'
const env = process.env.NEXT_PUBLIC_RIKSJA_WEB_ENV ?? 'dev'
const version = process.env.NEXT_PUBLIC_RIKSJA_WEB_VERSION
const apiUrl = apiUrlForEnv[env]

const rumEnabled = process.env.NODE_ENV !== 'development' && Boolean(apiUrl)

const initRum = (tracedApiUrl: string) => {
  // `init()` is a no-op-with-warning when called twice, so bail if a previous
  // mount (or a draft-mode toggle) already started the SDK.
  if (datadogRum.getInitConfiguration()) return

  // Start with consent withheld: the SDK collects nothing until Cookiebot tells us the
  // visitor accepted the `statistics` category. syncTrackingConsent() flips it on/off.
  datadogRum.init({
    applicationId: DATADOG_CONFIG.applicationId,
    clientToken: DATADOG_CONFIG.clientToken,
    site: DATADOG_CONFIG.site,
    service: `${entity}-web`,
    env: toDatadogEnv(env),
    version,
    sessionSampleRate: 10,
    sessionReplaySampleRate: 0,
    trackUserInteractions: false,
    trackResources: true,
    trackLongTasks: false,
    allowedTracingUrls: [{ match: tracedApiUrl, propagatorTypes: ['tracecontext'] }],
    trackingConsent: 'not-granted',
  })

  datadogRum.setGlobalContextProperty('team', 'riksja')
}

// The browser SDK has no true de-init, so this is the closest equivalent:
// withholding consent stops all collection and clears the stored session, and
// stopSession() ends the session that was already running.
const stopRum = () => {
  if (!datadogRum.getInitConfiguration()) return

  datadogRum.setTrackingConsent('not-granted')
  datadogRum.stopSession()
}

// Datadog RUM is an analytics tool, so it may only run once the visitor has accepted
// Cookiebot's `statistics` category. Reading it here keeps RUM in sync with live consent.
const syncTrackingConsent = () => {
  const consent = window.Cookiebot?.consent
  if (!consent) return
  datadogRum.setTrackingConsent(consent.statistics ? 'granted' : 'not-granted')
}

type DatadogRumProps = {
  /**
   * Draft mode means an editor is previewing unpublished content through Sanity's
   * Presentation tool. Those page views aren't real visits — they'd pollute RUM's
   * sessions, timings and errors (the preview iframe re-fetches uncached, stega-annotated
   * content) and burn quota — so RUM is never initialised for them.
   */
  isDraftMode: boolean
}

export const DatadogRum = ({ isDraftMode }: DatadogRumProps) => {
  useEffect(() => {
    if (!rumEnabled || !apiUrl) return

    if (isDraftMode) {
      stopRum()
      return
    }

    initRum(apiUrl)

    // Cookiebot may load before or after this effect runs; sync immediately in case
    // consent was already resolved, and on every subsequent consent change.
    syncTrackingConsent()
    window.addEventListener('CookiebotOnConsentReady', syncTrackingConsent)
    return () => window.removeEventListener('CookiebotOnConsentReady', syncTrackingConsent)
  }, [isDraftMode])

  return null
}
